import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import supabase from '../supabase/supabase';
import { clearOfflinePolicies } from '../hooks/useOfflinePolicies';
import { takeInviteToken } from '../auth/inviteToken';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

// Normalizes a `policies` row from the CRM database into the shape the
// portal components expect. The CRM stores `carrier` / `annual_premium`;
// the UI reads `vendor` / `premium`.
const mapPolicies = (policies) =>
  (policies || []).map((p) => ({
    ...p,
    policyNumber: p.policy_number || '',
    policyType: p.policy_type || '',
    coverageType: p.coverage_type || '',
    vendor: p.carrier || '',
    carrier: p.carrier || '',
    premium: toNumber(p.annual_premium),
    annualPremium: toNumber(p.annual_premium),
    expirationDate: p.expiration_date,
    effectiveDate: p.effective_date,
    paymentFrequency: p.payment_frequency || 'annual',
    autoRenewal: Boolean(p.auto_renewal),
    insuredItems: Array.isArray(p.insured_items) ? p.insured_items : [],
  }));

// Which policy rows a client should see in the portal.
//
// Renewals are entered as NEW policy rows and the CRM lags at renewal time, so
// an `Expired` row does NOT mean the client is uninsured -- it usually means
// their renewal has not been keyed in yet. Filtering to status='Active' alone
// showed nothing to 2,201 of the 2,364 clients who have a policy (93%): the
// empty-glovebox bug. Expired rows are surfaced and PolicyCard badges them
// "Expired" from the expiration date, so nothing is presented as current that
// is not. `Cancelled` is a real ending, and stays out.
export const VISIBLE_POLICY_STATUSES = ['Active', 'Expired'];

const fetchUserWithProfile = async (authUser) => {
  // Fetch from client_profiles (portal user table)
  const { data: profile } = await supabase
    .from('client_profiles')
    .select('*')
    .eq('id', authUser.id)
    .maybeSingle();

  // Use the direct client_id link from the profile to look up policies
  let policies = [];
  const clientId = profile?.client_id;
  if (clientId) {
    const { data: policyRows } = await supabase
      .from('policies')
      .select('*')
      .eq('client_id', clientId)
      .in('status', VISIBLE_POLICY_STATUSES)
      // Soft-deleted rows were never filtered. Today every soft-deleted row is
      // also Expired, so the old status filter hid them by accident; widening
      // the allowlist removes that accident, so exclude them explicitly.
      .is('deleted_at', null);
    policies = policyRows || [];
  }

  return {
    ...authUser,
    firstName: profile?.first_name || authUser.user_metadata?.first_name || '',
    lastName: profile?.last_name || authUser.user_metadata?.last_name || '',
    phone: profile?.phone || authUser.user_metadata?.phone || '',
    email: authUser.email || profile?.email || '',
    role: 'client',
    referralCode: profile?.referral_code || null,
    clientId: clientId || null,
    policies: mapPolicies(policies),
  };
};

/**
 * Redeems a staff-issued invite, if one is waiting from /claim.
 *
 * The RPC takes ONLY the token -- the client id lives on the invite row
 * server-side and is never sent from here, so a tampered browser cannot bind
 * itself to someone else's record. Returns true when a link was made, so the
 * caller knows to re-read the profile.
 *
 * Failures are deliberately quiet: an expired or already-used invite leaves
 * the account in the pending state, which is a state the app handles properly.
 * Never log the token or the email.
 */
const redeemPendingInvite = async () => {
  const token = takeInviteToken();
  if (!token) return false;
  try {
    const { data, error } = await supabase.rpc('redeem_client_invite', { p_token: token });
    if (error) return false;
    return Boolean(data?.linked);
  } catch {
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Mirrors user?.id for callbacks that outlive the render that created them.
  // The auth listener below is registered once, so it would otherwise close
  // over a stale user and clear the wrong client's cache on sign-out.
  const currentUserIdRef = useRef(null);
  useEffect(() => {
    currentUserIdRef.current = user?.id ?? null;
  }, [user]);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        // An invite stashed by /claim binds here, before the first read, so a
        // freshly linked client never sees the pending state by mistake.
        await redeemPendingInvite();
        const userWithProfile = await fetchUserWithProfile(session.user);
        setUser(userWithProfile);
      }
      setIsLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Skip PASSWORD_RECOVERY — handled locally in ResetPasswordPage
        if (event === 'PASSWORD_RECOVERY') return;
        // An actual sign-out, wherever it was performed: this tab, another
        // tab, another device, or a revoked session. The client said leave,
        // so the saved policies go with them.
        //
        // A session that merely goes null WITHOUT this event -- a failed
        // token refresh on a phone with no signal -- deliberately keeps the
        // cache. Clearing there would wipe proof of insurance at exactly the
        // roadside moment this app exists for.
        if (event === 'SIGNED_OUT') {
          clearOfflinePolicies(currentUserIdRef.current);
        }
        (async () => {
          if (session?.user) {
            await redeemPendingInvite();
            const userWithProfile = await fetchUserWithProfile(session.user);
            setUser(userWithProfile);
          } else {
            setUser(null);
          }
        })();
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email, password) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) return { success: false, error: error.message };

      const userWithProfile = await fetchUserWithProfile(data.user);
      setUser(userWithProfile);
      return { success: true, role: userWithProfile.role };
    } catch (err) {
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (data) => {
    setIsLoading(true);
    try {
      const { data: authData, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            first_name: data.firstName,
            last_name: data.lastName,
            phone: data.phone,
          },
        },
      });
      if (error) {
        if (error.message.toLowerCase().includes('already registered')) {
          return { success: false, error: 'already_exists' };
        }
        return { success: false, error: error.message };
      }

      // If session exists (no email confirmation required), create the profile row
      if (authData.session && authData.user) {
        await supabase.from('client_profiles').upsert({
          id: authData.user.id,
          email: data.email,
          first_name: data.firstName,
          last_name: data.lastName,
          phone: data.phone || '',
        }, { onConflict: 'id' });
      }

      const needsEmailConfirmation = !authData.session;
      return { success: true, needsEmailConfirmation, email: authData.user?.email };
    } catch (err) {
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  };

  const resendConfirmationEmail = async (email) => {
    const { error } = await supabase.auth.resend({ type: 'signup', email });
    if (error) return { success: false, error: error.message };
    return { success: true };
  };

  const requestPasswordReset = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) return { success: false, error: error.message };
    return { success: true };
  };

  const updatePassword = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return { success: false, error: error.message };
    return { success: true };
  };

  const logout = async () => {
    // Drop the locally cached policies first. They are the client's carrier,
    // policy numbers and premiums sitting in localStorage; on a shared or
    // family phone the next person to open the app must not find them.
    clearOfflinePolicies(user?.id);
    await supabase.auth.signOut();
    setUser(null);
  };

  const updateUser = async (updatedData) => {
    const { error } = await supabase
      .from('client_profiles')
      .upsert({
        id: user.id,
        email: updatedData.email || user.email,
        first_name: updatedData.firstName,
        last_name: updatedData.lastName,
        phone: updatedData.phone,
      }, { onConflict: 'id' });

    if (error) throw new Error(error.message);
    setUser((prev) => ({ ...prev, ...updatedData }));
  };

  // Ensure a client_profiles row exists after email confirmation
  const ensureProfile = async () => {
    if (!user) return;
    const { data: existing } = await supabase
      .from('client_profiles')
      .select('id')
      .eq('id', user.id)
      .maybeSingle();
    if (!existing) {
      await supabase.from('client_profiles').insert({
        id: user.id,
        email: user.email,
        first_name: user.firstName || '',
        last_name: user.lastName || '',
        phone: user.phone || '',
      });
      // Reload profile
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const updated = await fetchUserWithProfile(session.user);
        setUser(updated);
      }
    }
  };

  const value = {
    user,
    isLoading,
    login,
    signup,
    resendConfirmationEmail,
    requestPasswordReset,
    updatePassword,
    logout,
    updateUser,
    ensureProfile,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
